import dot from 'dot';

import String from '@root/components/string';

/*
 * A class defining a group of checkboxes alongside a code and a name.
 */
export class CheckboxGroup {
    code: string;
    name: string;
    identifier: string;
    map: string;
    hidden: boolean;
    readonly: boolean;
    reversed: boolean;
    required: boolean;
    codes: Checkbox[];
    checkbox: () => HTMLInputElement;

    select() {
        if (this.checkbox())
            this.checkbox().checked = true;
    }

    deselect() {
        if (this.checkbox())
            this.checkbox().checked = false;
    }

    getSelectedCodes() : string[] {
        if (this.reversed) {
            if (this.codes.length === 0)
                return !this.checkbox().checked ? [this.code] : [];

            const codes = this.codes
                .filter((checkbox: Checkbox) => !checkbox.selected())
                .map((checkbox: Checkbox) => checkbox.code);

            return codes.length === this.codes.length ? [this.code] : codes;
        } else {
            if (this.codes.length === 0)
                return this.checkbox().checked ? [this.code] : [];

            const codes = this.codes
                .filter((checkbox: Checkbox) => checkbox.selected())
                .map((checkbox: Checkbox) => checkbox.code);

            return codes.length === this.codes.length ? [this.code] : codes;
        }
    }

    getSelectedCount() : number {
        return this.codes.filter((checkbox: Checkbox) => checkbox.selected()).length;
    }

    static fromRaw(raw: any, language = 'en') : CheckboxGroup {
        const lg = new CheckboxGroup();
        lg.code = raw.code;
        lg.name = raw.name;
        if (language == 'fr') {
            if (raw.name_fr) lg.name = raw.name_fr;
        }
        // Using just the name resulted in duplicate collisions, if further collisions
        // arise, may need an different approach
        //lg.identifier = String.toKebabCase(raw.name);
        lg.identifier = 'code-' + String.toKebabCase(raw.code);
        lg.map = raw.map;
        // When this optional property is available and true from the API, it will
        // hide this checkbox to support default codes that a user can't select/unselect
        lg.hidden = raw.hidden;
        // When this optional property is available and true from the API, it will
        // remove the edit ability
        lg.readonly = raw.readonly;
        // When this optional property is available and true from the API, the group
        // checkboxes will be reversed: codes are included by default, and removed when
        // unselected
        lg.reversed = raw.reversed;
        if (raw.codes) {
            lg.codes = raw.codes.map(c => Checkbox.fromRaw(c, lg, language));
        } else {
            lg.codes = [];
        }
        // When this optional property is available and true from the API, at least
        // one option must remain checked in the group
        lg.required = raw.required
        lg.checkbox = () => document.querySelector(`#${lg.identifier}-checkbox`);
        return lg;
    }
}

/*
 * A checkbox that contains a code and a name along with an optional CheckboxGroup parent.
 */
export class Checkbox {
    code: string;
    name: string;
    identifier: string;
    map: string;
    hidden: boolean;
    readonly: boolean;
    checkbox: () => HTMLInputElement;
    parent: CheckboxGroup | null;
    help: string;

    select() {
        this.checkbox().checked = true;
    }

    deselect() {
        this.checkbox().checked = false;
    }

    selected() {
        return this.checkbox().checked;
    }

    static fromRaw(raw: any, parent?: CheckboxGroup, language = 'en') : Checkbox {
        const c = new Checkbox();
        c.code = raw.code;
        c.name = raw.name;
        if (language == 'fr') {
            if (raw.name_fr) c.name = raw.name_fr;
        }
        // Using just the name resulted in duplicate collisions, if further collisions
        // arise, may need an different approach
        //c.identifier = String.toKebabCase(raw.name);
        c.identifier = 'code-' + String.toKebabCase(raw.code);
        c.map = raw.map;
        // When this optional property is available and true from the API, it will
        // hide this checkbox to support default codes that a user can't select/unselect
        c.hidden = raw.hidden;
        // When this optional property is available and true from the API, it will
        // remove the edit ability
        c.readonly = raw.readonly;
        c.checkbox = () => document.querySelector(`#${c.identifier}-checkbox`);
        c.parent = parent;
        // Help text to show in a pop-up for the checkbox option
        c.help = raw.help;
        if (language == 'fr') {
            if (raw.help_fr) c.help = raw.help_fr;
        }
        return c;
    }
}

/*
 * A list of CheckboxGroups.
 */
export class CheckboxGroupUtils {
    /*
     * Renders a list of checkbox groups into the DOM.
     */
    static render(container: Element, checkboxGroups: CheckboxGroup[], codes: string[], templateId: string, onChange?: (e: Event, selected: string[]) => void, expandGroups: boolean = false) : string[] {
        const templateElement = document.querySelector('#' + (templateId || 'checkbox-template'));
        if (templateElement) {
            const template = dot.template(document.querySelector('#' + (templateId || 'checkbox-template')).innerHTML.trim());

            container.innerHTML = checkboxGroups
                .map((locationGroup: CheckboxGroup) => template(locationGroup) as string)
                .reduce((prev: string, curr: string) => prev += curr, '');
            
            this.setEventHandlers(container, checkboxGroups, onChange);
            this.setFromAccount(checkboxGroups, codes, expandGroups);
            return this.getAllSelected(checkboxGroups);
        }
        
        return [];
    }

    /*
     * Selects checkboxes that match a given set of codes.
     */
    static setFromAccount(groups: CheckboxGroup[], codes: string[], expandGroups: boolean = false) {
        groups
            .filter((group: CheckboxGroup) => !group.reversed && codes.indexOf(group.code) > -1)
            .map((group: CheckboxGroup) => { group.select(); return group; })
            .flatMap((group: CheckboxGroup) => group.codes)
            .forEach((checkbox: Checkbox) => checkbox.select());

        groups
            .flatMap((group: CheckboxGroup) => group.codes)
            .filter((checkbox: Checkbox) => checkbox.parent.reversed ? codes.indexOf(checkbox.code) == -1 : codes.indexOf(checkbox.code) > -1)
            .forEach((checkbox: Checkbox) => checkbox.select());

        groups
            .forEach((group: CheckboxGroup) => {
                // Event groups are collapsed by default. If any options have been deselected, expand the
                // group section by calling the click() method on the corresponding group header.
                if (expandGroups || group.codes.length != group.codes.filter((checkbox: Checkbox) => checkbox.selected()).length) {
                    let groupHeader = document.querySelector(`#${group.identifier}-header`) as HTMLButtonElement;
                    if (groupHeader) {
                        groupHeader.click();
                    }
                }
            });
    }

    /*
     * Updates rendered checkbox groups with on change handlers.
     */
    static setEventHandlers(element: Element, checkboxGroups: CheckboxGroup[], onChange?: (e: Event, selected: string[]) => void) {
        checkboxGroups.forEach((checkboxGroup: CheckboxGroup) => {
            let groupCheckbox = element.querySelector(`#${checkboxGroup.identifier}-checkbox`);
            if (groupCheckbox) {
                groupCheckbox.addEventListener('change', e => {
                    checkboxGroup.codes.forEach((checkbox: Checkbox) => {
                        const checked = (e.target as HTMLInputElement).checked;
                        checkbox.checkbox().checked = checked;
                    });

                    if (onChange)
                        onChange(e, this.getAllSelected(checkboxGroups));
                });
            }

            let groupHeader = element.querySelector(`#${checkboxGroup.identifier}-header`);
            if (groupHeader) {
                groupHeader.addEventListener('click', e => {
                    const chevron = (e.target as HTMLButtonElement).querySelector('.toggle-chevron');
                    chevron.classList.toggle('fa-chevron-down');
                    chevron.classList.toggle('fa-chevron-up');
                })
            }

            checkboxGroup.codes.forEach((checkbox: Checkbox) => {
                checkbox.checkbox().addEventListener('change', e => {
                    const checked = (e.target as HTMLInputElement).checked;
                    checkbox.checkbox().checked = checked;

                    if (checkbox.parent.checkbox()) {
                        if (checkbox.parent.codes.every((c: Checkbox) => c.checkbox().checked))
                            checkbox.parent.checkbox().checked = true;
                        else if (checkbox.parent.codes.some((c: Checkbox) => !c.checkbox().checked))
                            checkbox.parent.checkbox().checked = false;
                    }

                    if (onChange)
                        onChange(e, this.getAllSelected(checkboxGroups));
                });
            });
        });
    }

    /*
     * Retrieves a list of all selected codes from the specified checkbox group list.
     */
    static getAllSelected(checkboxGroups: CheckboxGroup[]) : string[] {
        return checkboxGroups.flatMap((checkboxGroup: CheckboxGroup) => checkboxGroup.getSelectedCodes());
    }
}

/*
 * An interface wrapping account information returned from the API.
 */
export interface Account {
    dev_id: string;
    token: string;
    type: AccountType;
    reg_id: string;
    locations: string[];
    language: Languages;
    levels: string[];
    events: string[];
    named_locations: any[];
    added_contacts: any[];
}

/*
 * An enum representing account type.
 */
export enum AccountType {
    Email = 'email',
    Sms = 'sms',
    Voice = 'phone',
    Mobile = 'mobile'
}

/*
 * An interface encompassing an auth response from the API.
 */
export interface Auth {
    dev_id: number;
    token: string;
    new?: boolean;
}

/*
 * The interface covering fields in customers.json.
 */
export interface Customer {
    code: string;
    name: string;
    iframeUrlEnglish: string;
    iframeUrlFrench: string;
}

/*
 * An enum describing available languages.
 */
export enum Languages {
    English = 'en',
    French = 'fr'
}

/*
 * Describes a latitude and longitude.
 */
export interface Point {
    lat: number;
    lng: number;
}

/*
 *  Represents a location returned from the API.
 */
export interface Location {
    code: string;
    name: string;
}

/*
 *  Represents a named location returned from the API.
 */
export interface NamedLocation {
    id: number,
    codes: string[];
    name: string;
    point: Point;
    pointString: string;
    polygon: Point[];
    polygonString: string;
    label: string;
}